import { db, encryptObject, decryptObject, addToCache, patchCacheItem } from '../../view/shared/utilities/cache';
import { fetchVslas, fetchVsla } from '../actions/getVslas';
import { fetchMeeting } from '../actions/meetings/getMeeting';
import { fetchVslasMembers } from '../actions/members/getVslaMembers';
import { triggerAction } from '../constants';
import store from '../index';
import CryptoJS from 'crypto-js';
import { EXPENSE_TRANSACTION, INCOME_TRANSACTION, hydrateOfflineBalance } from '../reducers/transactions/actions';

// The Web Worker instance, will be exported later to support termination on unload
export let worker = null;

// Consolidated logging helpers for clarity
const log = (message, ...args) => console.log(`[Main Thread] ${message}`, ...args);
const logError = (message, ...args) => console.error(`[Main Thread] ${message}`, ...args);

// Helper function to dispatch status updates to the Redux store
const dispatchStatus = (status, error = null) => {
  store.dispatch({
    type: 'UPDATE_QUEUE_STATUS',
    payload: { status, error },
  });
};

const dispatchCategory = (category = null) => {
  store.dispatch({
    type: 'UPDATE_QUEUE_CATEGORY',
    payload: category,
  });
};



/**
 * Checks for pending items of a specific category in the offline database.
 * @param {string} currentUserId - The ID of the current user.
 * @param {string} category - The category to check (e.g., 'group', 'members', 'income-transactions').
 * @returns {Promise<boolean>} - True if pending items exist, false otherwise.
 */
export const hasPendingItems = async (currentUserId, category) => {
  const requests = await db.offlineRequests.toArray();
  const items = requests.filter(({ userId, entityType }) => {
    if (userId !== currentUserId) {
      logError(`Unauthorized user! ${currentUserId}`)
      return false;
    }

    const isTransaction = category.includes('transactions');
    if (isTransaction) {
      const transactionTypes = category === 'income-transactions' ? INCOME_TRANSACTION : EXPENSE_TRANSACTION;
      return transactionTypes.includes(entityType);
    } else {
      return entityType === category;
    }
  });
  log(`${category}: found (${items.length})`)
  return items.length > 0
}

/**
 * Adds a new request to the offline queue.
 * @param {object} request - The request object to store.
 * @param {string} userId - The ID of the current user.
 */
export const addRequestToQueue = async (request, userId) => {
  try {
    const salt = CryptoJS.lib.WordArray.random(128 / 8).toString();
    const encryptedBody = encryptObject(JSON.stringify(request), salt);

    const requestToStore = {
      ...request,
      encryptedBody,
      salt,
    };

    await addToCache(
      'offlineRequests',
      requestToStore,
      userId,
      request?.tempId
    );
    log(`Encrypted request for ${request.entityType} with tempId ${request.tempId} added to the queue.`);
  } catch (error) {
    logError('Failed to add request to queue:', error);
  }
};

/**
 * A placeholder function for updating the UI status of a request.
 * This can be expanded to handle different UI states based on the request type.
 * @param {object} request - The original request object.
 * @param {string} status - The new status ('processing', 'error', etc.).
 * @param {string} errorDetail - The error message, if applicable.
 */
const updateRequestStatusInUI = (request, status, errorDetail) => {
  // You can enhance this with a switch statement or more generic logic
  // to handle various UI updates based on entityType and status.
  // Example: dispatch(updateGenericEntity({ ... }));
  if (request.entityType === 'members') {
    store.dispatch({
      type: 'UPDATE_MEMBER_STATUS',
      payload: {
        tempId: request.tempId,
        syncStatus: status,
        error: errorDetail || null,
      },
    });
  } else if (request.entityType === 'group') {
    if (status === 'processing') {
      store.dispatch(triggerAction('IS_CREATING_VSLA', true));
    } else if (status === 'error') {
      store.dispatch(triggerAction('IS_CREATING_VSLA', false));
    }
  }
  dispatchStatus(status?.toLocaleUpperCase(), errorDetail ? { message: errorDetail } : null);
  dispatchCategory(request.entityType)
};

// Initializes and sets up the Web Worker and its message listener
const setupWorker = () => {
  if (worker) return;
  worker = new Worker(new URL('./queue.worker.js', import.meta.url));

  worker.addEventListener('error', (error) => {
    logError('Worker error:', error);
    dispatchStatus('ERROR', { message: 'Worker crashed.' });
  });

  log('Starting queue processing on a Web Worker...');

  worker.addEventListener('message', async (event) => {
    const { type, payload } = event.data;
    switch (type) {
      case 'HTTP_REQUEST_PROCESSED': {
        const { request, responseData, error } = payload;
        if (responseData) {
          log(`HTTP request ${request.tempId} was successful.`);
          if (request.reduxActions.success?.length) {
            let mappedPayloads = [{ ...responseData, tempId: request.tempId }];
            if (request.entityType === 'group') {
              const offlineRequests = await db.offlineRequests.toArray();
              const localMembers = offlineRequests.filter(({ userId, entityType }) => userId === store.getState().user?.id && entityType === 'members')
              const { group, group_wallet, member } = responseData;
              mappedPayloads = [
                { ...group, tempId: request.tempId },
                group_wallet,
                [
                  member,
                  ...localMembers.map(m => ({
                    ...m.payload.data,
                    cacheId: m.cacheId,
                    syncStatus: 'processing',
                    error: null
                  }))
                ]
              ];
              await db.table('groups').delete(request.tempId);
              store.dispatch(fetchVslas())
            } else if (request.entityType === 'members') {
              const groupRequest = await db.table('offlineRequests').where({
                entityType: 'group',
                userId: store.getState().user?.id,
                tempId: request.dependsOn
              }).first();
              if (groupRequest) {
                const decryptedString = decryptObject(groupRequest.encryptedBody, groupRequest.salt);
                const decryptedBody = JSON.parse(decryptedString);
                mappedPayloads[0].group_id = decryptedBody.id
              }
            }
            request.reduxActions.success.forEach((act, idx) => {
              store.dispatch({ ...act, payload: mappedPayloads[idx] });
            });
          }
        } else {
          logError(`HTTP request ${request.tempId} failed with error: ${JSON.stringify(error)}`);
          if (request?.reduxActions?.fail) {
            store.dispatch({ ...request.reduxActions.fail, payload: { tempId: request.tempId, error } });
          }
        }
        break;
      }
      case 'WS_REQUEST_DISPATCH': {
        const { request, decryptedBody } = payload;
        updateRequestStatusInUI(request, 'processing');
        store.dispatch({ ...request, payload: decryptedBody });
        await patchCacheItem('offlineRequests', [request.userId, request.entityType], {
          error: null,
          status: 'processing'
        })
        log(`WS Request ${request.cacheId} dispatched from main thread.`);
        break;
      }
      case 'QUEUE_STATUS': {
        if (payload.isUploading) {
          store.dispatch(triggerAction('TOGGLE_IS_UPLOADING', true));
        } else {
          store.dispatch(triggerAction('TOGGLE_IS_UPLOADING', false));
        }
        break;
      }
      case 'QUEUE_ERROR':
        dispatchStatus('ERROR', payload);
        logError('Worker reported a queue error:', payload);
        store.dispatch(triggerAction('TOGGLE_FAILED_TO_UPLOAD', true));
        break;
      case 'WORKER_INITIALIZED':
        log('Worker is ready.');
        break;
      case 'QUEUE_FINISHED': {
        dispatchStatus('IDLE');
        log('Worker finished processing all queue items. Flag reset.');
        store.dispatch(triggerAction('TOGGLE_IS_UPLOADING', false));

        const currentUserId = store.getState().user?.id;
        if (currentUserId) {
          const pendingRequests = await db.table('offlineRequests')
            .where('[userId+status]').equals([currentUserId, 'pending'])
            .or('[userId+status]').equals([currentUserId, 'error'])
            .count();

          if (pendingRequests === 0) {
            log('Queue is empty. Clearing offline balance.');
            store.dispatch({ type: 'CLEAR_OFFLINE_BALANCE' });
          } else {
            log(`Queue still has ${pendingRequests} items. Re-hydrating offline balance.`);
            store.dispatch(hydrateOfflineBalance());
          }
        }
        store.dispatch(fetchVslas());
        break;
      }
      case 'SYNC_CATEGORY': {
        log(`Sync completed for category: ${JSON.stringify(payload)}`);
        if (payload.isCompleted) {
          if (payload.category === 'group') {
            store.dispatch(fetchVslas())
          }
          else if (payload.category === 'meeting') {
            store.dispatch(fetchMeeting())
          }
          else if (payload.category === 'members') {
            store.dispatch(fetchVslasMembers())
          }
        }
        dispatchCategory(payload.category)
        break;
      }
      default:
        log('Unknown message type from worker:', event.data);
        dispatchStatus('ERROR', payload);
        dispatchCategory(payload.category)
    }
  });
};

/**
 * Initiates the offline queue processing.
 * @returns {Promise<void>}
 */
export const processOfflineQueue = async () => {
  const currentStatus = store.getState().queue.status;
  const isConnected = store.getState().isConnected;
  if (!isConnected) {
    log('Error: Internet connection.');
    return;
  }
  if (currentStatus === 'PROCESSING') {
    log('Processor is already running.');
    return;
  }

  const state = store.getState();
  const currentUserId = state.user?.id;

  if (!currentUserId) {
    log('No user logged in. Aborting queue processing.');
    return;
  }

  const pendingCount = await db.table('offlineRequests')
    .where('[userId+status]').equals([currentUserId, 'pending'])
    .count();

  if (pendingCount === 0) {
    log('No pending requests to process.');
    dispatchStatus('IDLE');
    return;
  }

  try {
    setupWorker();
    dispatchStatus('PROCESSING');
    log('Starting offline queue processing...');

    worker.postMessage({
      type: 'START_QUEUE',
      payload: {
        userId: currentUserId,
      }
    });
  } catch (err) {
    logError('Failed to start queue processing:', err);
    dispatchStatus('ERROR', { message: err.message });
  }
};

/**
 * Terminates the Web Worker and resets the queue state.
 */
export const resetQueue = () => {
  if (worker) {
    worker.terminate();
    worker = null;
  }
  dispatchStatus('IDLE');
  log('Queue processing state has been reset to IDLE.');
};
